/**
 * SphereTimelineEvent - Displays a single timeline event with type-specific content
 * Dispatches to event-type-specific renderers for rich display
 */

import React from 'react';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';
import SphereCreatedContent from './content/SphereCreatedContent';
import PerspectiveAssignedContent from './content/PerspectiveAssignedContent';
import SpherePropertyChangedContent from './content/SpherePropertyChangedContent';
import VelocityChangedContent from './content/VelocityChangedContent';
import ImpactChangedContent from './content/ImpactChangedContent';
// NOTE: there is deliberately no UrgencyChangedContent / GravityChangedContent. Stage 3 made
// urgency and gravity EDGE-SUBJECT ASPECTS, so their changes arrive as `aspect_observation` entries
// and render through AspectObservationContent. Both components were deleted rather than left on
// disk unreachable. Follow-up: AspectObservationContent renders them generically, so the old
// "Urgency changed from 2 to 5" phrasing is lost until it learns to special-case known aspects.
import SphereWorkflowTransitionContent from './content/SphereWorkflowTransitionContent';
import TaskCompletedContent from './content/TaskCompletedContent';
import StatusPropagatedContent from './content/StatusPropagatedContent';
import CheckInSubmittedContent from './content/CheckInSubmittedContent';
import FactorLifecycleContent from './content/FactorLifecycleContent';
import AspectObservationContent from './content/AspectObservationContent';
import AspectValidationContent from './content/AspectValidationContent';
import AspectAttachmentContent from './content/AspectAttachmentContent';
import AttachmentContent from './content/AttachmentContent';
import PermissionChangedContent from './content/PermissionChangedContent';
import StageActionContent from './content/StageActionContent';
import ArcLifecycleContent from './content/ArcLifecycleContent';
import GenericEventContent from './content/GenericEventContent';
import { getActivityEventTypeConfig } from '../../../config/activityEventTypes';
import './SphereTimelineEventMolecule.scss';

export interface SphereTimelineEventProps {
  event: TimelineEvent;
  expanded?: boolean;
  onExpand?: () => void;
  onClick?: (event: TimelineEvent) => void;
}

/**
 * Format timestamp as relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (timestamp: string, t: (key: string, options?: any) => string): string => {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffMs / 604800000);
  const diffMonths = Math.floor(diffMs / 2592000000);

  if (diffMinutes < 1) return t('timeline.time.justNow');
  if (diffMinutes < 60) return t('timeline.time.minutesAgo', { count: diffMinutes });
  if (diffHours < 24) return t('timeline.time.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('timeline.time.daysAgo', { count: diffDays });
  if (diffWeeks < 4) return t('timeline.time.weeksAgo', { count: diffWeeks });
  return t('timeline.time.monthsAgo', { count: diffMonths });
};

/**
 * Format timestamp as absolute time
 */
export const formatAbsoluteTime = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString();
};

/**
 * Renders event-type-specific content.
 *
 * Keyed on the Observation Ledger's `LedgerEntryType` values (EAINT-764 Stage 2 / OQ-3). This
 * switch previously matched the legacy PascalCase `TimelineEventType` vocabulary, which the API
 * stopped sending when the timeline moved onto `ledger_entries` — so EVERY entry fell through to
 * `GenericEventContent` with a raw, untranslated i18n key. Not a crash, which is why it survived:
 * the timeline rendered, just undifferentiated.
 *
 * `Record<TimelineEventType, ...>`-style exhaustiveness is deliberately NOT used here: the
 * `default` arm is load-bearing, because a graph-service deploy can introduce an entry type before
 * the frontend ships a component for it, and rendering something generic beats rendering nothing.
 */
const renderEventContent = (event: TimelineEvent): React.ReactNode => {
  switch (event.eventType) {
    case 'sphere_created':
      return <SphereCreatedContent event={event} />;
    case 'sphere_property_changed':
      return <SpherePropertyChangedContent event={event} />;
    case 'perspective_assigned':
    case 'perspective_unassigned':
      return <PerspectiveAssignedContent event={event} />;
    case 'check_in':
      return <CheckInSubmittedContent event={event} />;
    case 'arc_transition':
      return <SphereWorkflowTransitionContent event={event} />;
    case 'arc_lifecycle':
      return <ArcLifecycleContent event={event} />;
    case 'stage_action':
      return <StageActionContent event={event} />;

    // Four components (FactorDeactivated/Reactivated/Paused/Resumed) collapse here: the ledger has
    // ONE factor_lifecycle type carrying `content.transition`, so eventType can no longer select
    // between them.
    case 'factor_lifecycle':
      return <FactorLifecycleContent event={event} />;

    case 'aspect_observation':
      return <AspectObservationContent event={event} />;
    case 'aspect_validation':
      return <AspectValidationContent event={event} />;
    case 'aspect_attached':
    case 'aspect_detached':
      return <AspectAttachmentContent event={event} />;
    case 'attachment_added':
    case 'attachment_removed':
      return <AttachmentContent event={event} />;
    case 'permission_changed':
      return <PermissionChangedContent event={event} />;

    // Restored in Phase 6 (OQ-3) after Stage 2 dropped their writers with the Cosmos timeline.
    case 'status_propagated':
      return <StatusPropagatedContent event={event} />;
    case 'velocity_changed':
      return <VelocityChangedContent event={event} />;
    case 'impact_changed':
      return <ImpactChangedContent event={event} />;
    case 'task_completed':
      return <TaskCompletedContent event={event} />;

    default:
      return <GenericEventContent event={event} />;
  }
};

const SphereTimelineEventMolecule: React.FC<SphereTimelineEventProps> = ({
  event,
  onClick,
}) => {
  const { t } = useTranslation();

  const relativeTime = formatRelativeTime(event.timestamp, t);
  const absoluteTime = formatAbsoluteTime(event.timestamp);
  const eventConfig = getActivityEventTypeConfig(event.eventType);

  const handleClick = onClick ? () => onClick(event) : undefined;

  return (
    <button
      type="button"
      className="sphere-timeline-event"
      onClick={handleClick}
    >
      {/* Event Header: title + timestamp */}
      <div className="sphere-timeline-event__header">
        <h4 className="sphere-timeline-event__title">
          <i className={`pi ${eventConfig.icon} sphere-timeline-event__title-icon`} />
          {t(`timeline.eventTypes.${event.eventType}`)}
        </h4>
        <span className="sphere-timeline-event__timestamp" title={absoluteTime}>
          <i className="pi pi-clock" />
          {relativeTime}
        </span>
      </div>

      {/* Event-type-specific content */}
      <div className="sphere-timeline-event__body">
        {renderEventContent(event)}
      </div>
    </button>
  );
};

export default SphereTimelineEventMolecule;
